import React, { useState, useEffect } from "react";
import axios from "axios";
import Layout from "./Layout";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Download,
  Eye,
  Trash2,
  Users
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientsPage = ({ user, onLogout }) => {
  const [clients, setClients] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [hotelFilter, setHotelFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    hotel_id: "",
    executive_id: "",
    rfc: "",
    client_name: "",
    contact_name: "",
    contact_mobile: "",
    contact_office_phone: "",
    contact_email: "",
    client_status: "Prospect",
    notes: ""
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const clientStatuses = [
    "Prospect",
    "Contacted", 
    "Qualified",
    "Active",
    "Inactive"
  ];

  const getExecutiveName = (executiveId) => {
    if (!executiveId) return "Unassigned";
    const executive = executives.find(exec => exec.id === executiveId);
    return executive ? executive.name : "Unknown Executive";
  };

  const fetchClients = async () => {
    try {
      setLoading(true);
      let url = `${API}/clients?limit=1000`;
      
      const params = new URLSearchParams();
      if (hotelFilter && hotelFilter !== "all") params.append("hotel_id", hotelFilter);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (searchTerm) params.append("search", searchTerm);
      
      if (params.toString()) {
        url += `&${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const fetchHotels = async () => {
    try {
      const response = await axios.get(`${API}/hotels`);
      setHotels(response.data);
    } catch (error) {
      console.error("Error fetching hotels:", error);
      toast.error("Failed to fetch hotels");
    }
  };

  const fetchExecutives = async () => {
    try {
      // Both admins and executives need the executives list to display names correctly
      const response = await axios.get(`${API}/executives`);
      setExecutives(response.data);
    } catch (error) {
      console.error("Error fetching executives:", error);
      toast.error("Failed to fetch executives");
    }
  };

  useEffect(() => {
    fetchHotels();
    fetchExecutives();
  }, []);

  // Debounced effect for search and filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchClients();
    }, searchTerm ? 300 : 0); // 300ms debounce for search, immediate for filters

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, hotelFilter]);

  const resetForm = () => {
    setFormData({
      hotel_id: "",
      executive_id: "",
      rfc: "",
      client_name: "",
      contact_name: "",
      contact_mobile: "",
      contact_office_phone: "",
      contact_email: "",
      client_status: "Prospect",
      notes: ""
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.hotel_id) newErrors.hotel_id = "Hotel is required";
    if (!formData.client_name.trim()) newErrors.client_name = "Client name is required";
    if (!formData.contact_name.trim()) newErrors.contact_name = "Contact name is required";
    if (!formData.contact_mobile.trim()) newErrors.contact_mobile = "Mobile phone is required";
    if (!formData.contact_email.trim()) newErrors.contact_email = "Email is required";
    
    // Validate mobile format
    if (formData.contact_mobile && !/^\+52\d{10}$/.test(formData.contact_mobile.replace(/[^\d+]/g, ''))) {
      newErrors.contact_mobile = "Mobile must be in format +52XXXXXXXXXX";
    }
    
    // Validate email format
    if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = "Invalid email format";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleAddClient = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/clients`, formData);
      toast.success("Client added successfully");
      setShowAddDialog(false);
      resetForm();
      fetchClients();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to add client";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClient = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      console.log('Updating client with data:', formData);
      console.log('Selected client:', selectedClient);
      
      // Only send fields that have actually changed or are required for update
      const updateData = {};
      
      // Always include these fields as they might have been edited
      if (formData.client_name !== selectedClient.client_name) {
        updateData.client_name = formData.client_name;
      }
      if (formData.contact_name !== selectedClient.contact_name) {
        updateData.contact_name = formData.contact_name;
      }
      if (formData.contact_email !== selectedClient.contact_email) {
        updateData.contact_email = formData.contact_email;
      }
      if (formData.client_status !== selectedClient.client_status) {
        updateData.client_status = formData.client_status;
      }
      if (formData.notes !== selectedClient.notes) {
        updateData.notes = formData.notes;
      }
      
      // Handle RFC - only send if it's different and not empty
      if (formData.rfc !== selectedClient.rfc) {
        if (formData.rfc && formData.rfc.trim()) {
          updateData.rfc = formData.rfc;
        }
      }
      
      // Handle phone - only send if it's different
      if (formData.contact_mobile !== selectedClient.contact_mobile) {
        updateData.contact_mobile = formData.contact_mobile;
      }
      
      // Handle office phone - only send if it's different
      if (formData.contact_office_phone !== selectedClient.contact_office_phone) {
        updateData.contact_office_phone = formData.contact_office_phone;
      }
      
      // Handle executive assignment - this is the key field for admin reassignment
      if (formData.executive_id !== selectedClient.executive_id) {
        updateData.executive_id = formData.executive_id || "";
      }
      
      console.log('Sending update data:', updateData);
      
      const response = await axios.put(`${API}/clients/${selectedClient.id}`, updateData);
      console.log('Update response:', response);
      
      toast.success("Client updated successfully");
      setShowEditDialog(false);
      resetForm();
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      console.error('Error updating client:', error);
      console.error('Error response:', error.response);
      
      let errorMessage = "Failed to update client";
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (client) => {
    setSelectedClient(client);
    setFormData({
      hotel_id: client.hotel_id || "",
      executive_id: client.executive_id || "",
      rfc: client.rfc || "",
      client_name: client.client_name || "",
      contact_name: client.contact_name || "",
      contact_mobile: client.contact_mobile || "",
      contact_office_phone: client.contact_office_phone || "",
      contact_email: client.contact_email || "",
      client_status: client.client_status || "Prospect",
      notes: client.notes || ""
    });
    setShowEditDialog(true);
  };

  const openViewDialog = (client) => {
    setSelectedClient(client);
    setShowViewDialog(true);
  };

  const openDeleteDialog = (client) => {
    setClientToDelete(client);
    setShowDeleteDialog(true);
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/clients/${clientToDelete.id}`);
      toast.success("Client deleted successfully");
      setShowDeleteDialog(false);
      setClientToDelete(null);
      fetchClients();
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to delete client";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      if (hotelFilter && hotelFilter !== "all") params.append("hotel_id", hotelFilter);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      params.append("format", format);
      
      const response = await axios.post(`${API}/clients/export?${params.toString()}`, {}, {
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `clients_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.${format}`;
      
      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }
      
      // Create and trigger download
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || (format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv')
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Clients exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export clients");
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "status-badge status-active";
      case "Inactive":
        return "status-badge status-inactive";
      case "Prospect":
        return "status-badge status-prospect";
      case "Contacted":
        return "status-badge status-contacted";
      case "Qualified":
        return "status-badge status-qualified";
      default:
        return "status-badge";
    }
  };

  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel ? hotel.hotel_name : "Unknown Hotel";
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h2 className="page-title">Client Management</h2>
        <p className="page-description">
          Manage your clients and their information
        </p>
      </div>

      {/* Search and Filters */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div className="card-content">
          <div className="search-filters">
            <div className="search-input">
              <div style={{ position: "relative" }}>
                <Search 
                  size={20} 
                  style={{ 
                    position: "absolute", 
                    left: "0.75rem", 
                    top: "50%", 
                    transform: "translateY(-50%)", 
                    color: "#6b7280" 
                  }} 
                />
                <Input
                  placeholder="Search clients by RFC, name, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: "2.5rem" }}
                  data-testid="search-input"
                />
              </div>
            </div>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="filter-select" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {clientStatuses.map(status => (
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={hotelFilter} onValueChange={setHotelFilter}>
              <SelectTrigger className="filter-select" data-testid="hotel-filter">
                <SelectValue placeholder="Filter by hotel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hotels</SelectItem>
                {hotels.map(hotel => (
                  <SelectItem key={hotel.id} value={hotel.id}>
                    {hotel.hotel_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
              <DialogTrigger asChild>
                <Button 
                  onClick={() => {
                    resetForm();
                    setShowAddDialog(true);
                  }}
                  data-testid="add-client-button"
                >
                  <Plus size={20} style={{ marginRight: "0.5rem" }} />
                  Add Client
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New Client</DialogTitle>
                </DialogHeader>
                <ClientForm
                  formData={formData}
                  errors={errors}
                  hotels={hotels}
                  executives={executives}
                  clientStatuses={clientStatuses}
                  onInputChange={handleInputChange}
                  onSubmit={handleAddClient}
                  onCancel={() => setShowAddDialog(false)}
                  submitting={submitting}
                  isEdit={false}
                  userRole={user?.role}
                />
              </DialogContent>
            </Dialog>

            {user.role === "admin" && (
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <Button
                  variant="outline"
                  onClick={() => handleExport("xlsx")}
                  data-testid="export-xlsx-button"
                >
                  <Download size={16} style={{ marginRight: "0.5rem" }} />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport("csv")}
                  data-testid="export-csv-button"
                >
                  <Download size={16} style={{ marginRight: "0.5rem" }} />
                  CSV
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Clients ({clients.length})</h3>
        </div>
        <div className="card-content" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: "3rem", textAlign: "center" }}>
              <div className="loading-spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "1rem", color: "#6b7280" }}>Loading clients...</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="empty-state">
              <Users className="empty-state-icon" />
              <h3 className="empty-state-title">No clients found</h3>
              <p className="empty-state-description">
                {searchTerm || (statusFilter && statusFilter !== "all") || (hotelFilter && hotelFilter !== "all")
                  ? "Try adjusting your search filters"
                  : "Get started by adding your first client"
                }
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>RFC</th>
                    <th>Client Name</th>
                    <th>Contact</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Hotel</th>
                    <th>Executive</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((client) => (
                    <tr key={client.id}>
                      <td style={{ fontWeight: "600" }}>{client.rfc}</td>
                      <td>{client.client_name}</td>
                      <td>{client.contact_name}</td>
                      <td>{client.contact_mobile}</td>
                      <td>{client.contact_email}</td>
                      <td>{getHotelName(client.hotel_id)}</td>
                      <td>{getExecutiveName(client.executive_id)}</td>
                      <td>
                        <span className={getStatusBadgeClass(client.client_status)}>
                          {client.client_status}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => openViewDialog(client)}
                            className="btn-icon"
                            title="View details"
                            data-testid={`view-client-${client.id}`}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => openEditDialog(client)}
                            className="btn-icon edit"
                            title="Edit client"
                            data-testid={`edit-client-${client.id}`}
                          >
                            <Edit size={16} />
                          </button>
                          {user.role === "admin" && (
                            <button
                              onClick={() => openDeleteDialog(client)}
                              className="btn-icon delete"
                              title="Delete client"
                              data-testid={`delete-client-${client.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          <ClientForm
            formData={formData}
            errors={errors}
            hotels={hotels}
            executives={executives}
            clientStatuses={clientStatuses}
            onInputChange={handleInputChange}
            onSubmit={handleEditClient}
            onCancel={() => {
              setShowEditDialog(false);
              resetForm();
              setSelectedClient(null);
            }}
            submitting={submitting}
            isEdit={true}
            userRole={user?.role}
          />
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      {selectedClient && (
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Client Details</DialogTitle>
            </DialogHeader>
            <ClientDetails 
              client={selectedClient} 
              hotels={hotels}
              onClose={() => setShowViewDialog(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {user.role === "admin" && (
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Client</DialogTitle>
            </DialogHeader>
            <div>
              <p style={{ marginBottom: "1.5rem" }}>
                Are you sure you want to delete <strong>{clientToDelete?.client_name}</strong>? 
                This action cannot be undone.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}>
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteDialog(false)}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleDeleteClient}
                  disabled={submitting}
                  data-testid="confirm-delete-client"
                >
                  {submitting ? (
                    <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <div className="loading-spinner"></div>
                      Deleting...
                    </span>
                  ) : (
                    "Delete Client"
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Layout>
  );
};

// Client Form Component
const ClientForm = ({ 
  formData, 
  errors, 
  hotels, 
  executives,
  clientStatuses, 
  onInputChange, 
  onSubmit, 
  onCancel, 
  submitting, 
  isEdit,
  userRole
}) => (
  <div>
    <div className="form-grid">
      <div>
        <Label htmlFor="hotel_id">Hotel *</Label>
        <Select 
          value={formData.hotel_id} 
          onValueChange={(value) => onInputChange("hotel_id", value)}
        >
          <SelectTrigger className={errors.hotel_id ? "error" : ""}>
            <SelectValue placeholder="Select hotel" />
          </SelectTrigger>
          <SelectContent>
            {hotels.map(hotel => (
              <SelectItem key={hotel.id} value={hotel.id}>
                {hotel.hotel_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.hotel_id && <div className="error-message">{errors.hotel_id}</div>}
      </div>

      {/* Executive Assignment - Only visible for admins in edit mode */}
      {isEdit && userRole === "admin" && (
        <div>
          <Label htmlFor="executive_id">Assigned Executive</Label>
          <Select 
            value={formData.executive_id || "unassigned"} 
            onValueChange={(value) => onInputChange("executive_id", value === "unassigned" ? "" : value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select executive" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {executives.map(executive => (
                <SelectItem key={executive.id} value={executive.id}>
                  {executive.name} ({executive.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Display current executive for non-admin users or in view mode */}
      {isEdit && userRole !== "admin" && (
        <div>
          <Label>Current Executive</Label>
          <div className="p-2 bg-gray-50 rounded border">
            {formData.executive_id ? 
              executives.find(exec => exec.id === formData.executive_id)?.name || "Unknown Executive" : 
              "Unassigned"
            }
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="rfc">RFC</Label>
        <Input
          id="rfc"
          value={formData.rfc}
          onChange={(e) => onInputChange("rfc", e.target.value.toUpperCase())}
          className={errors.rfc ? "error" : ""}
          placeholder="e.g., ABC123456789 (optional)"
        />
        {errors.rfc && <div className="error-message">{errors.rfc}</div>}
      </div>

      <div>
        <Label htmlFor="client_name">Client Name *</Label>
        <Input
          id="client_name"
          value={formData.client_name}
          onChange={(e) => onInputChange("client_name", e.target.value)}
          className={errors.client_name ? "error" : ""}
          placeholder="Company name"
        />
        {errors.client_name && <div className="error-message">{errors.client_name}</div>}
      </div>

      <div>
        <Label htmlFor="contact_name">Contact Name *</Label>
        <Input
          id="contact_name"
          value={formData.contact_name}
          onChange={(e) => onInputChange("contact_name", e.target.value)}
          className={errors.contact_name ? "error" : ""}
          placeholder="Contact person name"
        />
        {errors.contact_name && <div className="error-message">{errors.contact_name}</div>}
      </div>

      <div>
        <Label htmlFor="contact_mobile">Mobile Phone *</Label>
        <Input
          id="contact_mobile"
          value={formData.contact_mobile}
          onChange={(e) => onInputChange("contact_mobile", e.target.value)}
          className={errors.contact_mobile ? "error" : ""}
          placeholder="+52XXXXXXXXXX"
        />
        {errors.contact_mobile && <div className="error-message">{errors.contact_mobile}</div>}
      </div>

      <div>
        <Label htmlFor="contact_office_phone">Office Phone</Label>
        <Input
          id="contact_office_phone"
          value={formData.contact_office_phone}
          onChange={(e) => onInputChange("contact_office_phone", e.target.value)}
          placeholder="Optional office phone"
        />
      </div>

      <div>
        <Label htmlFor="contact_email">Email *</Label>
        <Input
          id="contact_email"
          type="email"
          value={formData.contact_email}
          onChange={(e) => onInputChange("contact_email", e.target.value)}
          className={errors.contact_email ? "error" : ""}
          placeholder="contact@company.com"
        />
        {errors.contact_email && <div className="error-message">{errors.contact_email}</div>}
      </div>

      <div>
        <Label htmlFor="client_status">Status</Label>
        <Select 
          value={formData.client_status} 
          onValueChange={(value) => onInputChange("client_status", value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {clientStatuses.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="form-grid-full">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => onInputChange("notes", e.target.value)}
          placeholder="Additional notes or comments"
          rows={3}
        />
      </div>
    </div>

    <div style={{ display: "flex", justifyContent: "flex-end", gap: "1rem", marginTop: "1.5rem" }}>
      <Button variant="outline" onClick={onCancel} disabled={submitting}>
        Cancel
      </Button>
      <Button onClick={onSubmit} disabled={submitting}>
        {submitting ? (
          <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div className="loading-spinner"></div>
            {isEdit ? "Updating..." : "Adding..."}
          </span>
        ) : (
          isEdit ? "Update Client" : "Add Client"
        )}
      </Button>
    </div>
  </div>
);

// Client Details Component
const ClientDetails = ({ client, hotels, onClose }) => {
  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel ? hotel.hotel_name : "Unknown Hotel";
  };

  return (
    <div>
      <div style={{ display: "grid", gap: "1rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>RFC</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {client.rfc}
            </div>
          </div>
          <div>
            <Label>Status</Label>
            <div style={{ marginTop: "0.25rem" }}>
              <span className={`status-badge status-${client.client_status.toLowerCase()}`}>
                {client.client_status}
              </span>
            </div>
          </div>
        </div>

        <div>
          <Label>Client Name</Label>
          <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {client.client_name}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Contact Name</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {client.contact_name}
            </div>
          </div>
          <div>
            <Label>Hotel</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {getHotelName(client.hotel_id)}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Mobile Phone</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {client.contact_mobile}
            </div>
          </div>
          <div>
            <Label>Office Phone</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {client.contact_office_phone || "N/A"}
            </div>
          </div>
        </div>

        <div>
          <Label>Email</Label>
          <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
            {client.contact_email}
          </div>
        </div>

        {client.notes && (
          <div>
            <Label>Notes</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {client.notes}
            </div>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
          <div>
            <Label>Created At</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {new Date(client.created_at).toLocaleDateString()}
            </div>
          </div>
          <div>
            <Label>Updated At</Label>
            <div style={{ padding: "0.5rem", background: "#f9fafb", borderRadius: "6px", marginTop: "0.25rem" }}>
              {new Date(client.updated_at).toLocaleDateString()}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
        <Button onClick={onClose}>Close</Button>
      </div>
    </div>
  );
};

export default ClientsPage;